import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import { Loader2 } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { ScoreBadge } from './ScoreBadge';
import { PriorityBadge } from './PriorityBadge';
import { StatusBadge } from './StatusBadge';
import { TemperatureBadge } from './TemperatureBadge';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

function formatDate(value, locale) {
  if (!value) return '—';
  try {
    return format(parseISO(value.replace('Z', '+00:00')), 'dd/MM/yyyy', { locale });
  } catch {
    return '—';
  }
}

export function LeadTable({ leads, loading, selectedIds, onToggleSelect, onToggleSelectAll }) {
  const { language } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const allSelected = leads.length > 0 && leads.every((lead) => selectedIds.has(lead.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="text-center py-16 text-zinc-500 text-sm">
        {t('aiLeadScanner.table.noLeads')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox checked={allSelected} onCheckedChange={onToggleSelectAll} data-testid="lead-select-all" />
            </TableHead>
            <TableHead>{t('aiLeadScanner.table.businessName')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.sector')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.city')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.score')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.priority')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.status')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.temperature')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.lastContacted')}</TableHead>
            <TableHead>{t('aiLeadScanner.table.nextFollowUp')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((lead) => (
            <TableRow
              key={lead.id}
              className="cursor-pointer hover:bg-zinc-800/40"
              // state.fromSearch: para que "Volver" en LeadDetail.js restaure
              // los filtros actuales (?search=...&status=...) — no basta con
              // que vivan en la URL de esta página, LeadDetail necesita saber
              // de dónde vino para reconstruir esa URL al volver.
              onClick={() => navigate(`/internal/ai-lead-scanner/leads/${lead.id}`, {
                state: { fromSearch: location.search },
              })}
              data-testid={`lead-row-${lead.id}`}
            >
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds.has(lead.id)}
                  onCheckedChange={() => onToggleSelect(lead.id)}
                  data-testid={`lead-select-${lead.id}`}
                />
              </TableCell>
              <TableCell className="font-medium text-zinc-100">{lead.business_name}</TableCell>
              <TableCell className="text-zinc-400">
                {t(`aiLeadScanner.sector.${lead.sector}`)}
              </TableCell>
              <TableCell className="text-zinc-400">{lead.city || '—'}</TableCell>
              <TableCell><ScoreBadge score={lead.score} /></TableCell>
              <TableCell><PriorityBadge priority={lead.priority} /></TableCell>
              <TableCell><StatusBadge status={lead.status} /></TableCell>
              <TableCell><TemperatureBadge temperature={lead.temperature} /></TableCell>
              <TableCell className="text-zinc-400 text-sm">{formatDate(lead.last_contacted_at, locale)}</TableCell>
              <TableCell className="text-zinc-400 text-sm">{formatDate(lead.next_follow_up_at, locale)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
